export type PlayerId = 1 | 2;
export type GameMode = "classic" | "capture-the-flag" | "single-player";
export type SinglePlayerMode = "last-tank-standing" | "capture-the-flag";
export type ComputerDifficulty = "beginner" | "intermediate" | "advanced";
export type DifficultySelection = ComputerDifficulty | "automatic";
export type WinReason = "elimination" | "capture";
export type MatchPhase =
  | "waiting"
  | "countdown"
  | "playing"
  | "paused"
  | "gameover";

export interface Players<T> {
  1: T;
  2: T;
}

export interface Vec2 {
  x: number;
  y: number;
}

export interface Wall {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Arena {
  width: number;
  height: number;
  seed: string;
  generatorVersion: number;
  walls: Wall[];
  spawns: Players<Vec2 & { angle: number }>;
}

export interface TankState {
  playerId: PlayerId;
  x: number;
  y: number;
  angle: number;
  lives: number | null;
  invulnerableUntil: number;
  immobilizedUntil: number;
}

export interface ProjectileState {
  owner: PlayerId;
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface FlagState {
  owner: PlayerId;
  x: number;
  y: number;
  carrier: PlayerId | null;
}

export interface ComputerState {
  selectedDifficulty: DifficultySelection;
  effectiveDifficulty: ComputerDifficulty;
  performance: number;
  lastAdjustmentAt: number;
}

export interface InputState {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  axisX: number;
  axisY: number;
  sequence: number;
  receivedAt: number;
}

export interface GameState {
  version: 1;
  gameId: string;
  mode: GameMode;
  singlePlayerMode: SinglePlayerMode | null;
  round: number;
  tick: number;
  phase: MatchPhase;
  resumePhase: "countdown" | "playing" | null;
  serverTime: number;
  countdownEndsAt: number | null;
  winner: PlayerId | null;
  winReason: WinReason | null;
  arena: Arena;
  tanks: Players<TankState>;
  projectiles: Players<ProjectileState | null>;
  flags: Players<FlagState> | null;
  computer: ComputerState | null;
  connected: Players<boolean>;
  rematchReady: Players<boolean>;
}

export type GameEvent =
  | {
      type: "shot";
      playerId: PlayerId;
    }
  | {
      type: "impact";
      playerId: PlayerId;
      lives: number | null;
      invulnerableUntil: number;
    }
  | {
      type: "blocked";
      playerId: PlayerId;
    }
  | {
      type: "gameover";
      winner: PlayerId;
      reason: WinReason;
    }
  | {
      type: "flag-picked-up";
      playerId: PlayerId;
      flagOwner: PlayerId;
    }
  | {
      type: "flag-dropped";
      playerId: PlayerId;
      flagOwner: PlayerId;
    }
  | {
      type: "flag-returned";
      playerId: PlayerId;
      flagOwner: PlayerId;
    };

export const GAME_CONSTANTS = {
  arenaWidth: 1_200,
  arenaHeight: 800,
  tankRadius: 25,
  projectileRadius: 5,
  forwardSpeed: 170,
  reverseSpeed: 115,
  projectileSpeed: 520,
  lives: 3,
  invulnerabilityMs: 3_000,
  simulationHz: 30,
  snapshotHz: 20,
  inputLeaseMs: 250,
  flagRadius: 18,
  flagDropDistance: 48,
  flagCaptureRadius: 56,
} as const;

export const PLAYER_IDS: readonly PlayerId[] = [1, 2];

export function otherPlayer(playerId: PlayerId): PlayerId {
  return playerId === 1 ? 2 : 1;
}

export function isGameMode(value: unknown): value is GameMode {
  return (
    value === "classic" ||
    value === "capture-the-flag" ||
    value === "single-player"
  );
}

export function isSinglePlayerMode(value: unknown): value is SinglePlayerMode {
  return value === "last-tank-standing" || value === "capture-the-flag";
}

export function isComputerDifficulty(
  value: unknown,
): value is ComputerDifficulty {
  return (
    value === "beginner" ||
    value === "intermediate" ||
    value === "advanced"
  );
}

export function isDifficultySelection(
  value: unknown,
): value is DifficultySelection {
  return value === "automatic" || isComputerDifficulty(value);
}

export function emptyInput(now = 0): InputState {
  return {
    forward: false,
    backward: false,
    left: false,
    right: false,
    axisX: 0,
    axisY: 0,
    sequence: 0,
    receivedAt: now,
  };
}
